import { ArrowLeft, CheckCircleFilled } from '@brandfolder/icons';
import { brandfolderColorPalette, StandardLink } from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, t } from '@lingui/macro';
import React, { FunctionComponent, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { object, string, boolean, SchemaOf } from 'yup';

import { useDialog } from '@components/common/custom_hooks/useDialog';

import {
  createCustomPermission,
  deleteCustomPermission,
  updateCustomPermission
} from '@api/v4/organizations/custom_permissions';

import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  WarningButton,
  TertiaryButton,
} from '@components/library/button';
import { StandardCheckbox } from '@components/library/checkbox';
import { StandardTextField } from '@components/library/text-field';
import { isGettyClient } from '@helpers/getty-strings';
import { defaultKnowledgeBaseLinks, gettyKnowledgeBaseLinks } from '@components/kb-article-links';

const CreateCustomRolePageWrapper = styled.div`
  background: ${brandfolderColorPalette.neutrals[0]};
  height: 100%;
  overflow-y: auto;
  padding: 24px 32px;
`;

const BackButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${brandfolderColorPalette.primary[700]};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: 4px;
  padding: 0;
  font-weight: 600;

  path {
    fill: ${brandfolderColorPalette.primary[700]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  color: ${brandfolderColorPalette.neutrals[700]};
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const ErrorMessage = styled.div`
  color: ${brandfolderColorPalette.error[600]};
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.2;
`;

const SectionTitle = styled.h3`
  color: ${brandfolderColorPalette.neutrals[700]};
  font-size: 20px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

const PermissionGroup = styled.div`
  margin-bottom: 16px;
`;

const PermissionItem = styled.div`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 0;
  display: flex;
  margin-bottom: 12px;
  padding: 0;
`;

const PermissionContent = styled.div`
  flex: 1;
  margin-left: 8px;
`;

const PermissionTitle = styled.div`
  color: ${brandfolderColorPalette.neutrals[700]};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
`;

const PermissionDescription = styled.div`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 12px;
  line-height: 1.3;
`;

const DefaultCapabilities = styled.div`
  background-color: transparent;
  border: none;
  border-radius: 0;
  margin-top: 32px;
  padding: 0;
`;

const DefaultCapabilitiesTitle = styled.div`
  align-items: center;
  color: ${brandfolderColorPalette.neutrals[700]};
  display: flex;
  font-family: 'Circular-Medium', 'Arial', 'Trebuchet MS', sans-serif;
  font-size: 14px;
  font-weight: 500;
  gap: 4px;
  margin: 0 0 8px 0;
`;

const CapabilityList = styled.ul`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 12px;
  margin: 0;
  padding-left: 16px;

  li {
    font-size: 12px;
    line-height: 1.3;
    margin-bottom: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 32px;
  padding-top: 16px;
`;

const RightButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledStandardCheckbox = styled(StandardCheckbox)`
  width: auto !important;
`;

const StyledCheckCircleFilled = styled(CheckCircleFilled)`
  path {
    fill: #1C806B;
  }
`;

const RoleNameInput = styled(StandardTextField)`
  width: 400px;

  .standard--input {
    background-color: ${brandfolderColorPalette.neutrals[50]};
    border: 1px solid ${brandfolderColorPalette.neutrals[300]};

    &:hover {
      border-color: ${brandfolderColorPalette.neutrals[400]};
    }

    &:focus, &:active {
      background-color: ${brandfolderColorPalette.neutrals[0]};
      border-color: ${brandfolderColorPalette.primary[500]};
    }
  }
`;

const ConfirmationModalContent = styled.div`
`;

const ConfirmationText = styled.p`
  color: ${brandfolderColorPalette.neutrals[700]};
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

interface CreateCustomRoleFormValues {
  roleName: string;
  asset_approve: boolean;
  add_to_shares: boolean;
  view_asset_usage: boolean;
  edit_brandguide: boolean;
}

interface CreateCustomRolePageProps {
  onBack: () => void;
  organizationKey?: string;
  existingRole?: {
    key: string;
    label: string;
    asset_approve: boolean;
    add_to_shares: boolean;
    view_asset_usage: boolean;
    edit_brandguide: boolean;
  };
  userCount?: number;
  onSave?: () => void;
  onDelete?: () => void;
}

const customRoleValidationSchema: SchemaOf<CreateCustomRoleFormValues> = object().shape({
  roleName: string()
    .required(t`Role name is required`)
    .max(25, t`Role name must be 25 characters or less`)
    .matches(
      /^[a-zA-Z]([a-zA-Z0-9]* ?)*$/,
      t`Role name must start with a letter and contain only letters, numbers, and spaces`
    ),
  asset_approve: boolean().required(),
  add_to_shares: boolean().required(),
  view_asset_usage: boolean().required(),
  edit_brandguide: boolean().required()
});

export const CreateCustomRolePage: FunctionComponent<CreateCustomRolePageProps> = ({
  onBack,
  organizationKey = BFG.organization_key,
  existingRole = undefined,
  userCount = 0,
  onSave = undefined,
  onDelete = undefined,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setValue,
    watch
  } = useForm<CreateCustomRoleFormValues, unknown, CreateCustomRoleFormValues>({
    defaultValues: {
      roleName: existingRole?.label || '',
      asset_approve: existingRole?.asset_approve || false,
      add_to_shares: existingRole?.add_to_shares || false,
      view_asset_usage: existingRole?.view_asset_usage || false,
      edit_brandguide: existingRole?.edit_brandguide || false
    },
    resolver: yupResolver(customRoleValidationSchema),
    mode: 'onChange'
  });

  const watchedValues = watch();
  const { openDialog, closeDialog } = useDialog();
  const orgKey = organizationKey ||
    (window as unknown as { BFG?: { organization_key?: string } }).BFG?.organization_key;

  const knowledgeBaseUrl = isGettyClient()
    ? gettyKnowledgeBaseLinks.userPermissionsLink
    : defaultKnowledgeBaseLinks.userPermissionsLink;


  const handlePermissionChange = (
    permission: keyof Omit<CreateCustomRoleFormValues, 'roleName'>
  ): void => {
    setValue(permission, !getValues(permission));
  };

  const handleFormSubmit = async (values: CreateCustomRoleFormValues): Promise<void> => {
    if (!orgKey) {
      return;
    }

    setIsLoading(true);

    try {
      const { roleName, ...permissions } = values;
      if (existingRole?.key) {
        // Update existing role
        await updateCustomPermission(orgKey, existingRole.key, {
          label: roleName.trim(),
          ...permissions,
        });
      } else {
        // Create new role
        await createCustomPermission(orgKey, {
          label: roleName.trim(),
          ...permissions,
        });
        try {
          // Signal to UsageInfo flow to show the onboarding tooltip
          const storageKey = `bf_new_custom_role_created_${orgKey}`;
          window.localStorage?.setItem(storageKey, '1');
        } catch (_) {
          // ignore storage errors silently
        }
      }
      onBack();
      onSave?.();
      window.dispatchEvent(new CustomEvent('customRoleChanged'));
    } catch (error) {
      console.error('Error saving custom role:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirmation = (): void => {
    // Skip confirmation modal if no users are affected
    if (userCount === 0) {
      void handleDelete();
      return;
    }

    const confirmationContent = (
      <ConfirmationModalContent>
        <ConfirmationText>
          <Trans>
            {userCount} users with {existingRole?.label || 'custom'} permissions will be downgraded to Collaborator permissions when you delete this role.
          </Trans>
        </ConfirmationText>
        <ModalButtonGroup>
          <TertiaryButton onClick={closeDialog}>
            <Trans>Cancel</Trans>
          </TertiaryButton>
          <PrimaryButton
            disabled={isDeletingRole}
            onClick={() => void handleDelete()}
          >
            {isDeletingRole ? <Trans>Deleting...</Trans> : <Trans>Delete role</Trans>}
          </PrimaryButton>
        </ModalButtonGroup>
      </ConfirmationModalContent>
    );

    openDialog(confirmationContent, {
      id: 'delete-role-confirmation',
      title: t`Delete this role`,
      showFooter: false,
      width: 550,
    });
  };

  const handleDelete = async (): Promise<void> => {
    if (!orgKey || !existingRole?.key) {
      return;
    }

    setIsDeletingRole(true);

    try {
      await deleteCustomPermission(orgKey, existingRole.key);

      closeDialog();
      onDelete?.();
      onBack();
      window.dispatchEvent(new CustomEvent('customRoleChanged'));
    } catch (error) {
      console.error('Error deleting custom role:', error);
    } finally {
      setIsDeletingRole(false);
    }
  };

  return (
    <CreateCustomRolePageWrapper>
      <BackButton onClick={onBack} type='button'>
        <ArrowLeft />
        <Trans>Back</Trans>
      </BackButton>

      <PageHeader>
        <PageTitle>
          {existingRole ? <Trans>Edit custom role</Trans> : <Trans>Create custom role</Trans>}
        </PageTitle>
        <PageDescription>
          <Trans>
            This custom role includes all the capabilities of the Collaborator
            role by default. You can rename it and add more capabilities as
            needed.
          </Trans>
        </PageDescription>
      </PageHeader>

      <FormSection>
        <Controller
          name="roleName"
          control={control}
          render={({ field }) => (
            <div>
              <StandardTextField
                {...field}
                id="role-name"
                label={t`Name`}
                placeholder={t`Collaborator Plus`}
              />
              {errors.roleName?.message && (
                <ErrorMessage>
                  {errors.roleName.message}
                </ErrorMessage>
              )}
            </div>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>
          <Trans>Set capabilities</Trans>
        </SectionTitle>
        <PageDescription style={{ marginBottom: '12px' }}>
          <Trans>Select the capabilities you want to give to this role.</Trans>
        </PageDescription>

        <PermissionGroup>
          <PermissionItem>
            <StyledStandardCheckbox
              checked={watchedValues.asset_approve}
              onChange={() => handlePermissionChange('asset_approve')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Approve assets</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>
                  Ability to approve or unapprove assets for approval workflow initiated in Brandfolder.
                </Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>

          <PermissionItem>
            <StyledStandardCheckbox
              checked={watchedValues.add_to_shares}
              onChange={() => handlePermissionChange('add_to_shares')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Manage share links</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>
                  Ability to view, add, remove assets to any share link. 
                </Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>

          <PermissionItem>
            <StyledStandardCheckbox
              checked={watchedValues.view_asset_usage}
              onChange={() => handlePermissionChange('view_asset_usage')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>View assets insight</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>Ability to view usage tab for asset insights</Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>



          <PermissionItem>
            <StyledStandardCheckbox
              checked={watchedValues.edit_brandguide}
              onChange={() => handlePermissionChange('edit_brandguide')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Edit Brandguide</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>Ability to edit a Brandguide</Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>
        </PermissionGroup>

        <DefaultCapabilities>
          <DefaultCapabilitiesTitle>
            <StyledCheckCircleFilled height={16} viewBox="0 0 16 16" width={16} />
            <Trans>Capabilities for this role by default:</Trans>
          </DefaultCapabilitiesTitle>
          <CapabilityList>
            <li>
              <Trans>Get Brandfolder embed code</Trans>
            </li>
            <li>
              <Trans>Create/rename sections on the Brandfolder level</Trans>
            </li>
            <li>
              <Trans>Create/rename/delete pinned searches</Trans>
            </li>
            <li>
              <Trans>Add/edit/delete assets</Trans>
            </li>
            <li>
              <Trans>Merge assets into one asset container</Trans>
            </li>
            <li>
              <Trans>View/add comments</Trans>
            </li>
            <li>
              <Trans>
                Set availability & expire assets at the Brandfolder level (does not apply to Collection level)
              </Trans>
            </li>
            <li>
              <Trans>
                Change the type & size (width & height, maintaining aspect
                ratio)
              </Trans>
            </li>
            <li>
              <Trans>Video: edit, play, clip, add a watermark, change file type & resolution</Trans>
            </li>
            <li>
              <Trans>
                Access templates, edit form fields, edit template settings
              </Trans>
            </li>
          </CapabilityList>
        </DefaultCapabilities>
        <StandardLink href={knowledgeBaseUrl} target="_blank" style={{ color: '#727682', display: 'block', marginTop: 8, textDecoration: 'underline', fontSize: '13px' }}>
          <Trans>Learn more about user permissions in Brandfolder</Trans>
        </StandardLink>
      </FormSection>

      <ButtonGroup>
        {existingRole ? <WarningButton
          disabled={isDeletingRole || isLoading}
          onClick={handleDeleteConfirmation}
        >
          <Trans>Delete</Trans>
        </WarningButton> : <div></div>}
        <RightButtons>
          <TertiaryButton
            disabled={isLoading || isDeletingRole}
            onClick={onBack}
          >
            <Trans>Back</Trans>
          </TertiaryButton>
          <PrimaryButton
            disabled={isLoading || isDeletingRole || !isValid}
            onClick={handleSubmit(handleFormSubmit)}
          >
            {isLoading ?
              (existingRole ?
              <Trans>Updating...</Trans> :
              <Trans>Creating...</Trans>) :
              (existingRole ?
              <Trans>Update role</Trans> :
              <Trans>Create role</Trans>)}
          </PrimaryButton>
        </RightButtons>
      </ButtonGroup>
    </CreateCustomRolePageWrapper>
  );
};
